import { request, saveBrowserKeyForBackground } from './api-client';
import { loadSession } from './session';
export interface EmailConnection {
  sync?: { id: string; status: string; imported: number } | null;
  id: string;
  accountEmail: string;
  status: string;
  revision: number;
  lastSyncedAt: string | null;
  lastError: string | null;
  replyExclusions?: string[];
  autoDraft: boolean;
  fundingAuthorized: boolean;
  replyLimitMicrounits: string;
  outreachLimitMicrounits: string;
}
export interface EmailConnections {
  canConnect?: boolean;
  setupIssue?: { code: string; message: string } | null;
  unsupportedAddressCount?: number;
  usageByPurpose?: Array<{ purpose: string; spent: string }>;
  budgets?: Array<{
    connectionId: string;
    mode: string;
    spent: string;
    reserved: string;
  }>;
  sendReceipts?: Array<{ id: string; status: string; createdAt: string }>;
  fundingAudits?: Array<{
    taskId: string;
    mode: string;
    reservedMicrounits: string;
    state: string;
  }>;
  enabled: boolean;
  sendsEnabled: boolean;
  draftingEnabled: boolean;
  policy: string;
  eligibleAddressCount: number;
  connections: EmailConnection[];
}
export interface EmailProvenance {
  origin?: string;
  automationId?: string;
  automationName?: string;
  enrollmentId?: string;
  stepNumber?: number;
  templateId?: string;
  templateVersion?: number;
  renderMode?: string;
  threadMode?: string;
  editedFromTemplate?: boolean;
}
export interface EmailDraft {
  provenance?: EmailProvenance;
  id: string;
  revision: number;
  sourceRevision: number;
  from: string;
  to: string[];
  cc: string[];
  subject: string;
  bodyText: string;
  edited: boolean;
  status: string;
}
export interface EmailReview {
  sendability?: { allowed: boolean; reason: string } | null;
  provenance?: EmailProvenance;
  id: string;
  kind: string;
  revision: number;
  sourceRevision: number;
  status: string;
  outcome: string;
  reason: string | null;
  draft: EmailDraft | null;
  generation: {
    id: string;
    status: string;
    lastError: string | null;
    reservedMicrounits: string;
    replacement?: {
      outcome: string;
      bodyText?: string;
      subject?: string;
      reason: string;
    } | null;
  } | null;
  sendAttempt: { id: string; status: string; lastError: string | null; sendAt?: string | null } | null;
}
export interface EmailThread {
  records?: Array<{ recordId: string; displayName: string; address: string }>;
  id: string;
  connectionId: string;
  revision: number;
  subject: string;
  lastActivityAt: string;
  unreadCount: number;
  gmailUrl: string;
  review: EmailReview | null;
}
export interface EmailMessage {
  direction?: string;
  id: string;
  providerId: string;
  receivedAt: string;
  sender: Array<{ address: string; name?: string }>;
  recipients: Array<{ address: string }>;
  cc: Array<{ address: string }>;
  bodyText: string;
  subject: string;
  labels: string[];
  truncated: boolean;
  attachments: Array<{ name: string; mimeType: string; size: number }>;
}
export interface EmailDetail {
  recentContext?: Array<{
    sender: Array<{ address: string }>;
    bodyText: string;
    receivedAt: string;
  }>;
  id: string;
  connectionId: string;
  revision: number;
  subject: string;
  missingHistory: boolean;
  healthy: boolean;
  gmailUrl: string;
  messages: EmailMessage[];
  nextCursor: string | null;
  review: EmailReview | null;
}
export interface EmailPage {
  items: EmailThread[];
  nextCursor: string | null;
}
export function emailKey() {
  const b = loadSession()?.bootstrap;
  return ['email', b?.workspace.id, b?.user?.id] as const;
}
export const emailApi = {
  connections: () => request<EmailConnections>('/v1/email/connections'),
  start: async (returnUrl: string, replySetup?: { fundingAuthorized: true; replyLimitMicrounits: number }) => {
    if (replySetup) await saveBrowserKeyForBackground();
    return request<{ authorizationUrl: string }>(
      '/v1/email/connections/google/start',
      { method: 'POST', body: { returnUrl, ...(replySetup ? { replySetup } : {}) } },
    );
  },
  settings: async (id: string, body: Record<string, unknown>) => {
    if (body.autoDraft === true || body.fundingAuthorized === true) await saveBrowserKeyForBackground();
    return request<EmailConnections>(`/v1/email/connections/${id}`, { method: 'PATCH', body });
  },
  disconnect: (id: string) =>
    request(`/v1/email/connections/${id}`, { method: 'DELETE' }),
  sync: (id: string) =>
    request<{ syncRunId: string; joinedExistingRun: boolean }>(
      `/v1/email/connections/${id}/sync`,
      { method: 'POST' },
    ),
  syncRun: (id: string) =>
    request<{ status: string; lastError: string | null }>(
      `/v1/email/sync-runs/${id}`,
    ),
  list: (recordId?: string, cursor?: string) =>
    request<EmailPage>(
      `${recordId ? `/v1/records/${recordId}/emails` : '/v1/email/review-items'}${cursor ? `?cursor=${cursor}` : ''}`,
    ),
  thread: (id: string, cursor?: string) =>
    request<EmailDetail>(
      `/v1/email/threads/${id}${cursor ? `?cursor=${cursor}` : ''}`,
    ),
  done: (id: string, expectedRevision: number) =>
    request(`/v1/email/review-items/${id}/done`, {
      method: 'POST',
      body: { expectedRevision },
    }),
  markRead: (id: string, messageIds: string[]) =>
    request(`/v1/email/threads/${id}/mark-read`, {
      method: 'POST',
      body: { messageIds },
    }),
  draft: (id: string, replyAll: boolean, instructions = '') =>
    request(`/v1/email/threads/${id}/draft`, {
      method: 'POST',
      body: { replyAll, instructions },
      personalKey: true,
    }),
  edit: (id: string, body: Record<string, unknown>) =>
    request(`/v1/email/drafts/${id}`, { method: 'PATCH', body }),
  resolve: (id: string, disposition: 'sent' | 'not_sent') =>
    request(`/v1/email/send-attempts/${id}/resolve`, {
      method: 'POST',
      body: { confirmedAfterCheckingGmail: true, disposition },
    }),
  send: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; status: string; sendAt?: string | null }>(`/v1/email/drafts/${id}/send`, {
      method: 'POST',
      body,
    }),
  cancelSchedule: (id: string) =>
    request(`/v1/email/send-attempts/${id}/cancel-schedule`, { method: 'POST' }),
};
